import React, { useState, useEffect, useCallback, Fragment } from "react";
import { Badge, CardBody, Col, Container, Form, FormGroup, FormLabel, Row, Table, Pagination, Button } from "react-bootstrap";
import Icon1 from "../../../images/orders/order-card-icon-1.svg";
import Icon2 from "../../../images/orders/order-card-icon-2.svg";
import Icon3 from "../../../images/orders/order-card-icon-3.svg";
import Icon4 from "../../../images/orders/order-card-icon-4.svg";
import Icon5 from "../../../images/orders/order-card-icon-5.svg";
import ViewIcon from "../../../images/view-icon.svg";
import SearchIcon from "../../../images/search-icon.svg";
import ShortMain from "../../../images/short-icon-main.svg"
import ShortUp from "../../../images/short-icon-top.svg"
import ShortDown from "../../../images/short-icon-bottom.svg"
import { FormControl, InputGroup } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { ordersApi, GetOrdersParams } from "../Orders/OrdersApi.tsx";
import { toast } from "react-toastify";
import { useApiLoader } from "../../../hooks/useApiLoader.tsx";
import { HiOutlineDownload } from 'react-icons/hi';
import DateRangeSelector from "../../DateRangeSelector";
import ExportModal from "../../ExportModal";
import { exportPosthumousGifts, downloadFile, getExportFilename } from '../../../utils/exportUtils';
import "./Orders.scss";

interface OrderCounts {
  totalGifts: number;
  upcomingGifts: number;
  processingGifts: number;
  shippedGifts: number;
  deliveredGifts: number;
}

interface SortConfig {
  sortBy: string;
  sortOrder: 'asc' | 'desc' | '';
}

interface DateRange {
  startDate: string;
  endDate: string;
}

interface PosthumousOrder {
  _id: string;
  orderId: string;
  orderDate: string;
  userName: string;
  recipient: string | null;
  occasion: string;
  giftType: string;
  giftStatus: string;
  deliveryDate: string | null;
  recurring: string;
}

const PosthumousGifts = () => {
  const { withLoader } = useApiLoader();
  const navigate = useNavigate();
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [giftTypeFilter, setGiftTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    sortBy: '',
    sortOrder: ''
  });
  const [showExportModal, setShowExportModal] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: '',
    endDate: ''
  });
  const [orders, setOrders] = useState<PosthumousOrder[]>([]);
  const [orderCounts, setOrderCounts] = useState<OrderCounts>({
    totalGifts: 0,
    upcomingGifts: 0,
    processingGifts: 0,
    shippedGifts: 0,
    deliveredGifts: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Format date for API (YYYY-MM-DD format)
  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  };

  // Fetch posthumous gifts from API (using conditional gifts endpoint)
  const fetchPosthumousGifts = useCallback(async (
    search: string = '',
    status: string = '',
    page: number = 1,
    limit: number = itemsPerPage,
    giftType: string = '',
    sortBy: string = 'orderDate',
    sortOrder: string = 'desc',
    startDate: string = '',
    endDate: string = ''
  ) => {
    try {
      setLoading(true);

      const params: GetOrdersParams = {
        search: search,
        page: page,
        limit: limit,
        giftStatus: status || undefined,
        giftType: giftType || undefined,
        sortBy: sortBy || 'orderDate',
        sortOrder: (sortOrder === 'asc' || sortOrder === 'desc') ? sortOrder : undefined,
        // Add date range parameters (already formatted in YYYY-MM-DD)
        ...(startDate && { startDate: startDate }),
        ...(endDate && { endDate: endDate }),
      };

      const response = await withLoader(
        () => ordersApi.getAllUnverifiedPostMortemGifts(params),
        'Loading posthumous gifts...'
      );

      if (response.status === 'Success' && response.data) {
        setOrders(response.data);
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalCount(response.pagination.totalCount);
        }
      } else {
        toast.error(response.message || 'Failed to fetch posthumous gifts');
        setOrders([]);
        setTotalPages(1);
        setTotalCount(0);
      }
    } catch (error: any) {
      console.error('Error fetching posthumous gifts:', error);
      toast.error(error.message || 'Failed to fetch posthumous gifts');
      setOrders([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch order counts (reuse from main orders)
  const fetchOrderCounts = useCallback(async () => {
    try {
      const response = await withLoader(
        () => ordersApi.getOrderCounts(),
        'Loading posthumous gift statistics...'
      );
      if (response.status === 'Success' && response.giftCounts) {
        setOrderCounts(response.giftCounts);
      }
    } catch (error: any) {
      console.error('Error fetching order counts:', error);
    }
  }, []);

  // Load orders and counts on component mount
  useEffect(() => {
    fetchPosthumousGifts('', '', 1, itemsPerPage, '', sortConfig.sortBy, sortConfig.sortOrder, '', '');
    fetchOrderCounts();
    setCurrentPage(1);
  }, [fetchPosthumousGifts, fetchOrderCounts]);

  // Handle search with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      fetchPosthumousGifts(searchTerm, statusFilter, 1, itemsPerPage, giftTypeFilter, sortConfig.sortBy, sortConfig.sortOrder, dateRange.startDate, dateRange.endDate);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchPosthumousGifts, itemsPerPage, giftTypeFilter]);

  // Handle status filter separately without debouncing
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    fetchPosthumousGifts(searchTerm, statusFilter, 1, itemsPerPage, giftTypeFilter, sortConfig.sortBy, sortConfig.sortOrder, dateRange.startDate, dateRange.endDate);
  }, [statusFilter, itemsPerPage, giftTypeFilter]);

  // Handle gift type filter separately without debouncing
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    fetchPosthumousGifts(searchTerm, statusFilter, 1, itemsPerPage, giftTypeFilter, sortConfig.sortBy, sortConfig.sortOrder, dateRange.startDate, dateRange.endDate);
  }, [giftTypeFilter]);

  // Handle page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchPosthumousGifts(searchTerm, statusFilter, page, itemsPerPage, giftTypeFilter, sortConfig.sortBy, sortConfig.sortOrder, dateRange.startDate, dateRange.endDate);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
    fetchPosthumousGifts(searchTerm, statusFilter, 1, newItemsPerPage, giftTypeFilter, sortConfig.sortBy, sortConfig.sortOrder, dateRange.startDate, dateRange.endDate);
  };

  // Handle stats card click to filter orders
  const handleStatsClick = (status: string) => {
    setStatusFilter(status === 'Total Orders' ? '' : status);
    setCurrentPage(1);
    fetchPosthumousGifts(searchTerm, status === 'Total Orders' ? '' : status, 1, itemsPerPage, giftTypeFilter, sortConfig.sortBy, sortConfig.sortOrder, dateRange.startDate, dateRange.endDate);
  };

  // Enhanced sorting logic with three states
  const handleSort = (field: string) => {
    let newSortOrder: 'asc' | 'desc' = 'asc';

    if (sortConfig.sortBy === field) {
      if (sortConfig.sortOrder === 'asc') {
        newSortOrder = 'desc';
      } else if (sortConfig.sortOrder === 'desc') {
        // Reset sorting - no sort, maintain current page
        setSortConfig({ sortBy: '', sortOrder: '' });
        fetchPosthumousGifts(searchTerm, statusFilter, currentPage, itemsPerPage, giftTypeFilter, '', '', dateRange.startDate, dateRange.endDate);
        return;
      }
    }

    setSortConfig({ sortBy: field, sortOrder: newSortOrder });
    // Maintain current page when sorting
    fetchPosthumousGifts(searchTerm, statusFilter, currentPage, itemsPerPage, giftTypeFilter, field, newSortOrder, dateRange.startDate, dateRange.endDate);
  };

  // Get sort icon for table headers
  const getSortIcon = (field: string) => {
    if (sortConfig.sortBy !== field) {
      return (
        <span className='short-icon'>
          <img src={ShortMain} alt='' />
        </span>
      );
    }

    if (sortConfig.sortOrder === 'asc') {
      return (
        <span className='short-icon'>
          <img src={ShortUp} alt='' />
        </span>
      );
    } else {
      return (
        <span className='short-icon'>
          <img src={ShortDown} alt='' />
        </span>
      );
    }
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      // Format as MM/DD/YYYY to ensure year is always shown
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Format delivery date for display (MM/DD only)
  const formatDeliveryDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      // Format as MM/DD only
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Capitalize text helper
  const capitalizeText = (text: string) => {
    if (!text) return 'N/A';
    // Capitalize first letter of each word
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Convert backend gift type to user-friendly display name
  const getDisplayGiftType = (backendType: string) => {
    const typeMapping = {
      'PhysicalItem': 'Physical',
      'GiftCard': 'Coupon',
      'Monetary': 'Monetary',
      'Message': 'Message'
    };
    return typeMapping[backendType as keyof typeof typeMapping] || backendType;
  };

  // Get status badge - keeping original colors
  const getStatusBadge = (status: string) => {
    const displayType = getDisplayGiftType(status);
    let color = 'secondary';
    if (displayType === 'Physical') color = 'or-badge';
    else if (displayType === 'Coupon') color = 'blu-badge';
    else if (displayType === 'Message') color = 'gre-badge';
    else if (displayType === 'Monetary') color = 'succ-badge';
    return <Badge bg={color}>{displayType}</Badge>;
  };

  // Handle row click to navigate to posthumous gift details
  const handleRowClick = (orderId: string, event: React.MouseEvent) => {
    // Prevent navigation if clicking on the action buttons/links
    const target = event.target as HTMLElement;
    if (target.closest('.table-options') || target.closest('a')) {
      return;
    }
    navigate(`/posthumous-details/${orderId}`);
  };

  // Handle date range change
  const handleDateRangeChange = useCallback((dateRangeString: string, isCustom?: boolean) => {
    if (!dateRangeString || dateRangeString.trim() === '') {
      setDateRange({ startDate: '', endDate: '' });
      setCurrentPage(1);
      fetchPosthumousGifts(searchTerm, statusFilter, 1, itemsPerPage, giftTypeFilter, sortConfig.sortBy, sortConfig.sortOrder === '' ? 'desc' : sortConfig.sortOrder, '', '');
      return;
    }

    const dates = dateRangeString.split(' - ');
    if (dates.length === 2) {
      // Convert MM/DD/YYYY to YYYY-MM-DD without timezone conversion
      const parseDate = (dateStr: string): string => {
        const [month, day, year] = dateStr.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      };

      const formattedStartDate = parseDate(dates[0].trim());
      const formattedEndDate = parseDate(dates[1].trim());

      if (formattedStartDate > formattedEndDate) {
        toast.error('Start date cannot be after end date');
        return;
      }

      setDateRange({ startDate: formattedStartDate, endDate: formattedEndDate });
      setCurrentPage(1);

      fetchPosthumousGifts(searchTerm, statusFilter, 1, itemsPerPage, giftTypeFilter, sortConfig.sortBy, sortConfig.sortOrder === '' ? 'desc' : sortConfig.sortOrder, formattedStartDate, formattedEndDate);
    }
  }, [searchTerm, statusFilter, giftTypeFilter, sortConfig.sortBy, sortConfig.sortOrder, itemsPerPage, fetchPosthumousGifts]);

  // Handle export
  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Preparing Excel export...');
      const blob = await exportPosthumousGifts('excel', dateRange.startDate, dateRange.endDate);
      const filename = getExportFilename('Posthumous_Gifts', 'excel', dateRange.startDate, dateRange.endDate);
      downloadFile(blob, filename);
      toast.success('Excel file downloaded successfully!');
    } catch (error: any) {
      console.error('Export Excel error:', error);
      if (error.response?.status === 404) {
        toast.error('No posthumous gifts found in the selected date range.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid export parameters.');
      } else {
        toast.error('Failed to export Excel file. Please try again.');
      }
    }
  };

  const handleExportPdf = async () => {
    try {
      toast.info('Preparing PDF export...');
      const blob = await exportPosthumousGifts('pdf', dateRange.startDate, dateRange.endDate);
      const filename = getExportFilename('Posthumous_Gifts', 'pdf', dateRange.startDate, dateRange.endDate);
      downloadFile(blob, filename);
      toast.success('PDF file downloaded successfully!');
    } catch (error: any) {
      console.error('Export PDF error:', error);
      if (error.response?.status === 404) {
        toast.error('No posthumous gifts found in the selected date range.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid export parameters.');
      } else {
        toast.error('Failed to export PDF file. Please try again.');
      }
    }
  };

  return (
    <>
      <section className='main-body'>
        <Container fluid className="p-0">
          <CardBody>
          
          <div className="tb-lead">
            <div className="main-table">
              {/* Filter and Search Row */}
              <div className="mb-3 main-table-filter">
                <div className="row g-2 align-items-stretch">
                  {/* Search Field */}
                  <div className='col-lg-3 col-md-6 col-12'>
                    <div className='main-table-search h-100'>
                      <FormGroup className='form-group search-box mb-0 h-100'>
                        <InputGroup className="h-100">
                          <FormControl
                            type="text"
                            placeholder="Search posthumous gifts..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="h-100"
                          />
                          <InputGroup.Text>
                            <img src={SearchIcon} alt='' />
                          </InputGroup.Text>
                        </InputGroup>
                      </FormGroup>
                    </div>
                  </div>

                  {/* Filter Dropdown */}
                  <div className='col-lg-2 col-md-6 col-12'>
                    <div className='select-field h-100'>
                      <FormGroup className='form-group mb-0 h-100'>
                        <Form.Select
                          value={giftTypeFilter}
                          onChange={(e) => setGiftTypeFilter(e.target.value)}
                          className="h-100"
                        >
                          <option value="">Filter by Gift Type</option>
                          <option value="PhysicalItem">Physical</option>
                          <option value="GiftCard">Coupon</option>
                          <option value="Monetary">Monetary</option>
                          <option value="Message">Message</option>
                        </Form.Select>
                      </FormGroup>
                    </div>
                  </div>

                  {/* Spacer to push date range and export to the right */}
                  <div className='col-lg-3 d-none d-lg-block'></div>

                  {/* Date Range Picker */}
                  <div className='col-lg-3 col-md-9 col-12'>
                    <div className='calender-block h-100'>
                      <DateRangeSelector
                        onDateRangeChange={handleDateRangeChange}
                      />
                    </div>
                  </div>

                  {/* Export Button */}
                  <div className='col-lg-1 col-md-3 col-12'>
                    <Button
                      variant="primary"
                      onClick={handleExport}
                      className="d-flex align-items-center justify-content-center gap-2 h-100 w-100"
                    >
                      <HiOutlineDownload size={18} />
                      <span>Export</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive mt-4">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th
                      onClick={() => handleSort('orderId')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        Order ID
                        {getSortIcon('orderId')}
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort('orderDate')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        Order Date
                        {getSortIcon('orderDate')}
                      </span>
                    </th>
                    <th>Customer Name</th>
                    <th
                      onClick={() => handleSort('recipient')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        Recipient
                        {getSortIcon('recipient')}
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort('occasion')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        Occasion
                        {getSortIcon('occasion')}
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort('giftType')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        Gift Type
                        {getSortIcon('giftType')}
                      </span>
                    </th>
                    <th
                      onClick={() => handleSort('recurring')}
                      style={{ cursor: 'pointer' }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        Recurring
                        {getSortIcon('recurring')}
                      </span>
                    </th>
                    <th>
                      Delivery Date
                    </th>
                    <th className='actions' style={{ fontWeight: '600', fontSize: '0.875rem', minWidth: "160px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4">
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="ms-2">Loading posthumous gifts...</span>
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => (
                      <tr 
                        key={order._id}
                        onClick={(event) => handleRowClick(order._id, event)}
                        style={{ cursor: 'pointer' }}
                        className="clickable-row"
                      >
                        <td>{order.orderId}</td>
                        <td>{formatDate(order.orderDate)}</td>
                        <td>{capitalizeText(order.userName)}</td>
                        <td>{capitalizeText(order.recipient)}</td>
                        <td>{capitalizeText(order.occasion)}</td>
                        <td className="info-badge-area">
                          <p className="d-flex align-items-center gap-1 mb-0">
                            {getStatusBadge(order.giftType)}
                          </p>
                        </td>
                        <td>{order.recurring}</td>
                        <td>{formatDeliveryDate(order.deliveryDate)}</td>
                        <td>
                          <div className="d-flex gap-2 table-options">
                            <Link to={`/posthumous-details/${order._id}`}>
                              <img src={ViewIcon} alt="View" style={{ cursor: 'pointer' }} />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>
          {!loading && orders.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No posthumous gifts found matching your search criteria.</p>
            </div>
          )}
          
          {/* Pagination - Match Figma Design */}
          {!loading && orders.length > 0 && (
            <div className="posthumous-pagination-wrapper">
              <div className="pagination-info-section">
                <div className="pagination-showing">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries
                  {(dateRange.startDate || dateRange.endDate) && (
                    <span className="ms-2">(filtered by date range)</span>
                  )}
                </div>
              </div>
              
              <div className="pagination-controls-section">
                <div className="pagination-per-page">
                  <span className="pagination-label">Show</span>
                  <select 
                    className="form-select form-select-sm pagination-select" 
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                
                {totalPages > 1 && (
                  <Pagination className="mb-0 posthumous-pagination">
                    <Pagination.Prev 
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                    
                    {/* Show page numbers like in Figma: 1, 2, ... 5, 6 */}
                    {Array.from({ length: Math.min(totalPages, 4) }, (_, index) => {
                      let pageNumber;
                      if (totalPages <= 4) {
                        pageNumber = index + 1;
                      } else if (currentPage <= 2) {
                        // Show first 2 pages, then jump to last 2
                        pageNumber = index < 2 ? index + 1 : totalPages - (3 - index);
                      } else if (currentPage >= totalPages - 1) {
                        // Show first 2, then last 2
                        pageNumber = index < 2 ? index + 1 : totalPages - (3 - index);
                      } else {
                        // Show current page area
                        pageNumber = currentPage - 1 + index;
                      }
                      
                      // Add ellipsis logic
                      const showEllipsis = totalPages > 4 && 
                                         ((index === 1 && currentPage > 3) || 
                                          (index === 2 && currentPage < totalPages - 2));
                      
                      return (
                        <React.Fragment key={index}>
                          {showEllipsis && <span className="px-2">...</span>}
                          <Pagination.Item
                            key={pageNumber}
                            active={pageNumber === currentPage}
                            onClick={() => handlePageChange(pageNumber)}
                          >
                            {pageNumber}
                          </Pagination.Item>
                        </React.Fragment>
                      );
                    })}
                    
                    <Pagination.Next 
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                )}
              </div>
            </div>
          )}
        </CardBody>
      </Container>
      </section>

      {/* Export Modal */}
      <ExportModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        onExportExcel={handleExportExcel}
        onExportPdf={handleExportPdf}
        title="Export Posthumous Gifts Data"
      />
    </>
  );
};

export default PosthumousGifts;